/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useMemo } from 'react';

import { makeStyles, Typography, Grid, useMediaQuery } from '@material-ui/core';

import { ReactComponent as IconDownload } from '../../assets/images/ic-download.svg';
import { DOCS, DOCX, iconSetup, IMAGE } from '../../models/Resource';
import { downloadFile } from '../../utils/files';

const useStyles = makeStyles((theme) => ({
  additionalContainerEmpty: {
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: '5px',
    display: 'flex',
    justifyContent: 'center',
    marginTop: '8px',
    minHeight: '90px',
    padding: '16px',
  },
  additionalFileContainer: {
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  additionalFileName: {
    color: '#172B4D',
    fontSize: '14px',
    lineHeight: '18px',
    maxWidth: '250px',
    wordBreak: 'break-all',
  },
  additionalFileTitleContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  additionalIconImage: {
    height: '100%',
    marginRight: '12px',
    width: '24px',
  },
  additionalIconInteraction: {
    height: '16px',
    marginRight: '8px',
    width: '16px',
  },
  additionalIconInteractionDownload: {
    fill: theme.palette.primary.main,
    stroke: theme.palette.primary.main,
  },
  additionalInteractionText: {
    color: theme.palette.primary.main,
    fontSize: '16px',
    lineHeight: '18px',
    marginBottom: '0px',
  },
  additionalNoFilesFound: {
    marginBottom: '8px',
  },
  additionalSubtitle: {
    color: '#999999',
    fontSize: '14px',
    lineHeight: '18px',
    marginTop: '8px',
  },
  additionalViewMore: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'inline-block',
    fontSize: '16px',
    lineHeight: '18px',
    marginTop: '12px',
    padding: '0px',
  },
  downloadText: {
    display: 'flex',
    gap: '4px',
  },
  link: { alignItems: 'center', cursor: 'pointer', display: 'flex', justifyContent: 'center', marginRight: '8px' },
  title: {
    color: '#172B4D',
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '24px',
    marginBottom: '9px',
    textAlign: 'left',
  },
}));

/**
 * Custom Class Option
 * @param onViewAdditionalFile
 * @param onViewMoreAdditionalFiles
 * @param caseDetails
 * @param customClasses {{title: string, viewButton: string, viewMoreButton: string}}
 * @param shouldShowAllFiles {boolean}
 * @param shouldItemsFullWidth {boolean}
 * @returns {JSX.Element}
 */

export default ({ onViewAdditionalFile, onViewMoreAdditionalFiles, caseDetails, customClasses = {}, shouldShowAllFiles = false, shouldItemsFullWidth = false }) => {
  const classes = useStyles();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const additionalFiles = useMemo(() => {
    return caseDetails?.case?.additionalFiles || [];
  }, [caseDetails]);

  const handleViewAdditionalFile = useCallback(
    (file) => {
      if (onViewAdditionalFile) onViewAdditionalFile(file);
    },
    [onViewAdditionalFile]
  );

  const handleViewMoreAdditionalFiles = useCallback(
    (files) => {
      if (onViewMoreAdditionalFiles) onViewMoreAdditionalFiles(files);
    },
    [onViewMoreAdditionalFiles]
  );

  const renderedAdditionalFiles = useMemo(() => {
    if (!additionalFiles?.length) {
      return null;
    }

    const additionalFilesToRender = additionalFiles?.length > 3 && !shouldShowAllFiles ? additionalFiles.slice(0, 3) : additionalFiles;

    const mappedFiles = additionalFilesToRender.map((file) => {
      const { fileName = '', type, description, uuid, originalType } = file;
      const fileTitle = fileName || description || '';
      const isDocxFile = originalType === DOCX;

      const cleanFileName = fileTitle.startsWith('0-') ? fileTitle.slice(2, fileTitle.length) : fileTitle;
      const selectedIcon = iconSetup[type] || iconSetup.document;

      const gridSetup = {
        lg: isDesktop || shouldItemsFullWidth ? 12 : 3,
        md: isDesktop || shouldItemsFullWidth ? 12 : 4,
        sm: isDesktop || shouldItemsFullWidth ? 12 : 6,
        xs: isDesktop || shouldItemsFullWidth ? 12 : 12,
      };

      return (
        <Grid item xs={gridSetup.xs} sm={gridSetup.sm} md={gridSetup.md} lg={gridSetup.lg} key={uuid}>
          <div className={classes.additionalFileContainer}>
            {/* Icon Title */}
            <div className={classes.additionalFileTitleContainer}>
              <img src={selectedIcon} className={classes.additionalIconImage} alt="" />
              <Typography className={classes.additionalFileName}>{cleanFileName}</Typography>
            </div>

            {/* Button */}
            <div className={classes.downloadText}>
              {!isDocxFile && (
                <div
                  onClick={() => handleViewAdditionalFile(file)}
                  style={{ alignItems: 'center', cursor: 'pointer', display: 'flex', justifyContent: 'center', marginRight: '8px' }}>
                  <Typography className={`${classes.additionalInteractionText} ${customClasses?.viewButton || ''}`}>View</Typography>
                </div>
              )}
              {[DOCS, IMAGE].includes(file.type) && (
                <a onClick={() => downloadFile(file)} className={classes.link} style={{ textDecoration: 'none' }} rel="noreferrer">
                  <Typography className={classes.additionalInteractionText}>Download</Typography>
                  <IconDownload className={`${classes.additionalIconInteraction} ${classes.additionalIconInteractionDownload}`} />
                </a>
              )}
            </div>
          </div>
        </Grid>
      );
    });

    return (
      <>
        <Grid container spacing={1}>
          {mappedFiles}
        </Grid>

        {(additionalFiles?.length > 3 && !shouldShowAllFiles && (
          <Typography onClick={() => handleViewMoreAdditionalFiles(additionalFiles)} className={`${classes.additionalViewMore} ${customClasses?.viewMoreButton || ''}`}>
            View More
          </Typography>
        )) ||
          null}
      </>
    );
  }, [additionalFiles, classes, isDesktop, handleViewAdditionalFile, handleViewMoreAdditionalFiles]);

  if (!additionalFiles?.length) return null;
  return (
    <div style={{ textAlign: 'left' }}>
      <Typography className={`${classes.title} ${customClasses?.title || ''}`}>Additional Files</Typography>

      {renderedAdditionalFiles}
    </div>
  );
};
